import { useCallback, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import { liveStreamApi } from "@circle-react/api";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { isNetworkError } from "@circle-react/helpers/backendRequestHelpers";
import {
  PARTICIPANT_ROLES,
  isEnded,
  isScheduled,
  roomTypeData,
} from "@circle-react/helpers/liveStreamRoomHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Participant } from "@circle-react/types/Live/Participant";
import type { Room } from "@circle-react/types/Live/Room";
import { useToast } from "@circle-react-uikit/ToastV2";

const PRE_JOIN_REFETCH_INTERVAL = 10000;

export const useLiveStreamRoom = (roomSlug: string) => {
  const { error, success } = useToast();
  const [currentParticipant, setCurrentParticipant] =
    useState<Participant | null>(null);
  const [viewTypeOverride, setViewTypeOverride] = useState<string | null>(null);
  const {
    data: room,
    isLoading,
    isError,
    error: apiError,
    refetch: refetchRoom,
    ...passThroughProps
  } = useQuery<Room, ApiError>(
    internalApi.liveStreams.show({
      id: roomSlug,
    }),
    {
      refetchInterval: currentParticipant ? false : PRE_JOIN_REFETCH_INTERVAL,
      refetchIntervalInBackground: currentParticipant ? false : true,
    },
  );

  // Note: We're using both room.status and streamEnded state
  // to make sure we're able to react quickly without refetching the room
  // or in case fetching the room fails but stream is still ended.
  const [isStreamEnded, setIsStreamEnded] = useState<boolean>(false);
  const joinMutation = useMutation<Participant, ApiError, string>(
    role =>
      liveStreamApi.join({
        id: room?.id,
        role,
        forReactQuery: true,
      }) as Promise<Participant>,
    {
      onSuccess: async participant => {
        if (isScheduled(room)) {
          await refetchRoom();
        }
        setCurrentParticipant(participant);
      },
      onError: response => {
        const errorMessage = response.firstErrorDetail
          ? `${t("live_streams.room.join_error")}: ${response.firstErrorDetail}`
          : `${t("live_streams.room.join_error")}.`;
        error(errorMessage);
      },
    },
  );

  const leaveMutation = useMutation<Participant, ApiError, string | undefined>(
    reason =>
      liveStreamApi.leave({
        id: room?.id,
        participantId: currentParticipant?.id,
        reason,
      }),
    {
      onSuccess: participant => setCurrentParticipant(participant),
      onError: (err: unknown) => {
        if (isNetworkError(err)) {
          setCurrentParticipant(null);
        } else {
          error(t("live_streams.room.leave_error"));
        }
      },
    },
  );

  const goLiveMutation = useMutation(
    () => liveStreamApi.goLive({ id: room?.id, forReactQuery: true }),
    {
      onSuccess: () => refetchRoom(),
      onError: () => error(t("live_streams.room.go_live_error")),
    },
  );

  const endStreamMutationOptions = {
    onSuccess: () => {
      success(t(`live_streams.room.${roomTypeData(room)}.end_success`));
      setIsStreamEnded(true);
      void refetchRoom();
    },
    onError: () =>
      error(t(`live_streams.room.${roomTypeData(room)}.end_error`)),
  };

  const endStreamMutation = useMutation(
    () => liveStreamApi.endStream({ id: room?.id }),
    endStreamMutationOptions,
  );

  const endStreamDelayedMutation = useMutation(
    () => liveStreamApi.endStreamDelayed({ id: room?.id }),
    endStreamMutationOptions,
  );

  const updateRoomMutation = useMutation<
    unknown,
    ApiError,
    Record<string, unknown>
  >(formData => liveStreamApi.update({ id: room?.id, formData }), {
    onSuccess: () => {
      void refetchRoom();
      success(t(`live_streams.room.${roomTypeData(room)}.update_success`), {
        duration: "short",
        shouldUseProgress: false,
      });
    },
    onError: () =>
      error(t(`live_streams.room.${roomTypeData(room)}.update_error`)),
  });
  const updateRoomSilentlyMutation = useMutation<
    unknown,
    ApiError,
    Record<string, unknown>
  >(formData => liveStreamApi.update({ id: room?.id, formData }), {
    onSuccess: () => refetchRoom(),
  });
  const onEndStream = (reason?: string) => {
    success(reason || "");
    setIsStreamEnded(true);
    void refetchRoom();
  };

  const joinAsHost = useCallback(
    () => joinMutation.mutate(PARTICIPANT_ROLES.HOST),
    [joinMutation],
  );

  const joinAsParticipant = useCallback(
    () => joinMutation.mutate(PARTICIPANT_ROLES.HLS_SPECTATOR),
    [joinMutation],
  );

  return {
    room,
    isLoading,
    isError,
    apiError,
    joinAsHost,
    joinAsParticipant,
    isJoining: joinMutation.isLoading,
    currentParticipant,
    updateRoom: updateRoomMutation.mutate,
    updateRoomAsync: updateRoomMutation.mutateAsync,
    updateRoomSilentlyAsync: updateRoomSilentlyMutation.mutateAsync,
    goLive: goLiveMutation.mutate,
    goLiveAsync: goLiveMutation.mutateAsync,
    leave: leaveMutation.mutate,
    leaveAsync: leaveMutation.mutateAsync,
    endStream: endStreamMutation.mutate,
    endStreamDelayed: endStreamDelayedMutation.mutate,
    endStreamDelayedAsync: endStreamDelayedMutation.mutateAsync,
    onEndStream,
    isLeaving: leaveMutation.isLoading,
    refetchRoom,
    isStreamEnded: isEnded(room) || isStreamEnded,
    viewTypeOverride,
    setViewTypeOverride,
    ...passThroughProps,
  };
};
